/**
 * Activity export — pure client-side.
 *
 * Two reasons we don't ask the server to assemble the file:
 *   1. Private rows are E2E-encrypted; the server doesn't have the cleartext.
 *      The client must decrypt and serialise, full stop.
 *   2. Keeping the export client-side avoids creating a "give me all my data
 *      as plaintext" endpoint that becomes an attractive target for anyone
 *      who steals a session cookie.
 *
 * Output is markdown-flavoured text so it renders nicely in any editor or
 * markdown viewer, without committing us to a specific renderer.
 */
import { api } from './api';
import { session } from './session.svelte';
import {
  decryptPayload, base64ToBytes, type PrivatePayload,
} from './crypto';
import type { Activity } from '../../../shared/types';

function formatDate(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return '';
  const d = new Date(epochSeconds * 1000);
  return d.toLocaleString('nb-NO', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
}

interface NormalisedRow {
  title: string;
  description: string | null;
  tags: string[];
  loc_label: string | null;
  scheduled_at: number | null;
  visibility: 'private' | 'semi' | 'public' | 'friends';
}

/** Decrypt a private activity into the same shape as semi/public rows. */
function normalise(a: Activity, dek: Uint8Array | null): NormalisedRow | null {
  if (a.visibility === 'private') {
    if (!dek) return null;
    let payload: PrivatePayload;
    try {
      payload = decryptPayload(
        { ciphertext: base64ToBytes(a.ciphertext), nonce: base64ToBytes(a.nonce) },
        dek,
      );
    } catch {
      return null;
    }
    return {
      title: payload.title,
      description: payload.description ?? null,
      tags: payload.tags,
      loc_label: payload.loc_label ?? null,
      scheduled_at: payload.scheduled_at ?? null,
      visibility: 'private',
    };
  }
  return {
    title: a.title,
    description: a.description,
    tags: a.tags,
    loc_label: a.loc_label,
    scheduled_at: a.scheduled_at,
    visibility: a.visibility,
  };
}

function sectionBody(rows: NormalisedRow[]): string {
  return rows.map((r) => {
    const lines: string[] = [`### ${r.title}`];
    if (r.description) {
      // Indent the description with `> ` so the section header stays scannable.
      const quoted = r.description.split('\n').map((ln) => `> ${ln}`).join('\n');
      lines.push(quoted);
    }
    if (r.tags.length) lines.push(`Etiketter: ${r.tags.join(', ')}`);
    if (r.loc_label) lines.push(`Sted: ${r.loc_label}`);
    if (r.scheduled_at) lines.push(`Tidspunkt: ${formatDate(r.scheduled_at)}`);
    return lines.join('\n');
  }).join('\n\n');
}

/**
 * Build a markdown export of the caller's currently-visible activities and
 * trigger a download. Returns the size of the generated blob in bytes so
 * callers can show a quick confirmation.
 */
export async function downloadExport(): Promise<number> {
  const activities = await api.listActivities();
  const dek = session.dek;
  const normalised = activities
    .map((a) => normalise(a, dek))
    .filter((r): r is NormalisedRow => r !== null);

  const myPrivate = normalised.filter((r) => r.visibility === 'private');
  const mySemiPublic = normalised.filter(
    (r) => r.visibility === 'semi' || r.visibility === 'public',
  );

  const header = [
    `# Vinterliste — eksport`,
    session.user ? `Bruker: ${session.user.email}` : '',
    `Generert: ${new Date().toLocaleString('nb-NO', { hourCycle: 'h23' })}`,
    `Totalt: ${normalised.length} (${myPrivate.length} private, ${mySemiPublic.length} offentlige/anonyme synlige for deg)`,
  ].filter(Boolean).join('\n');

  const sections: string[] = [header];
  if (myPrivate.length) {
    sections.push(`\n## Private (${myPrivate.length})\n\n${sectionBody(myPrivate)}`);
  }
  if (mySemiPublic.length) {
    sections.push(`\n## Offentlige og anonyme (${mySemiPublic.length})\n\n${sectionBody(mySemiPublic)}`);
  }
  const text = sections.join('\n') + '\n';

  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    // Use ISO date in the filename so multiple exports don't overwrite.
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `vinterliste-${stamp}.md`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }

  return blob.size;
}
